import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { IndividualSessionFeedbackData } from '../../../../types/IndividualSessionFeedbackTypes'
import {
  LATEST_SESSION_FEEDBACK_READ_SECTION_IDS,
  LATEST_SESSION_FEEDBACK_SECTION_IDS
} from '../constants/latestSessionFeedbackSections'

export interface UseLatestSessionFeedbackGlowParams {
  feedbackData: IndividualSessionFeedbackData | undefined
  isLoading: boolean
  hasFetchError: boolean
  /** The boxes start covered while this game's feedback has not been read. */
  isUnread: boolean
  /** Called once the player has uncovered every box that makes the feedback read. */
  onRead: () => void
}

const getCustomCursorStyles = (isActive: boolean): CSSProperties =>
  isActive ? { cursor: 'url(/assets/svg/mouseCursor.svg) 16 16, auto' } : {}

/**
 * Glow / glint orchestration for the sections of latest-session feedback that has not been read yet.
 */
export function useLatestSessionFeedbackGlow({
  feedbackData,
  isLoading,
  hasFetchError,
  isUnread,
  onRead
}: UseLatestSessionFeedbackGlowParams) {
  const [showFirstVisitExperience, setShowFirstVisitExperience] = useState(false)
  const [pendingSections, setPendingSections] = useState<string[]>([])
  const [hasInitialGlowDelayElapsed, setHasInitialGlowDelayElapsed] = useState(false)
  const [glintActivity, setGlintActivity] = useState<Record<string, boolean>>({})
  const initialPendingSectionsRef = useRef<string[]>([])
  const pendingInitializedRef = useRef(false)
  const glowDelayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const glowReactivateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showFirstVisitExperienceRef = useRef(showFirstVisitExperience)
  const pendingSectionsRef = useRef(pendingSections)
  const feedbackDataRef = useRef<IndividualSessionFeedbackData | undefined>(undefined)

  useEffect(() => {
    showFirstVisitExperienceRef.current = showFirstVisitExperience
  }, [showFirstVisitExperience])

  useEffect(() => {
    pendingSectionsRef.current = pendingSections
  }, [pendingSections])

  useEffect(() => {
    feedbackDataRef.current = feedbackData
  }, [feedbackData])

  const renderedSectionIds = useMemo(() => {
    if (!feedbackData) return []

    const sections: string[] = []

    if (feedbackData.whatIsWorkingWell) sections.push(LATEST_SESSION_FEEDBACK_SECTION_IDS.whatsWorking)
    if (feedbackData.whatToWatchOut) sections.push(LATEST_SESSION_FEEDBACK_SECTION_IDS.watchOut)
    if (feedbackData.improvementIdea) sections.push(LATEST_SESSION_FEEDBACK_SECTION_IDS.improvementIdea)
    if (feedbackData.communicatedEmotion) sections.push(LATEST_SESSION_FEEDBACK_SECTION_IDS.emotion)

    return sections
  }, [feedbackData])

  useEffect(() => {
    if (isUnread) setShowFirstVisitExperience(true)
  }, [isUnread])

  useEffect(() => {
    if (!showFirstVisitExperience) {
      if (pendingSections.length > 0) {
        setPendingSections([])
      }
      initialPendingSectionsRef.current = []
      pendingInitializedRef.current = false
      return
    }

    if (renderedSectionIds.length === 0) {
      if (pendingSections.length > 0) {
        setPendingSections([])
      }
      initialPendingSectionsRef.current = []
      pendingInitializedRef.current = false
      return
    }

    setPendingSections((prev) => {
      const filteredPrev = prev.filter((id) => renderedSectionIds.includes(id))

      if (!pendingInitializedRef.current) {
        pendingInitializedRef.current = true
        initialPendingSectionsRef.current = renderedSectionIds

        return renderedSectionIds
      }

      const knownSections = initialPendingSectionsRef.current
      const newSections = renderedSectionIds.filter((id) => !knownSections.includes(id))

      if (newSections.length > 0) {
        initialPendingSectionsRef.current = [...knownSections, ...newSections]

        return [...filteredPrev, ...newSections]
      }

      if (filteredPrev.length !== prev.length) {
        return filteredPrev
      }

      return prev
    })
  }, [showFirstVisitExperience, renderedSectionIds, pendingSections.length])

  const onReadRef = useRef(onRead)
  onReadRef.current = onRead

  // Read once both feedback texts are uncovered; the other boxes may stay covered.
  useEffect(() => {
    if (!isUnread || !pendingInitializedRef.current) return

    const hasUncoveredEveryReadSection = LATEST_SESSION_FEEDBACK_READ_SECTION_IDS.every(
      (sectionId) => !pendingSections.includes(sectionId)
    )
    if (hasUncoveredEveryReadSection) onReadRef.current()
  }, [isUnread, pendingSections])

  useEffect(() => {
    if (showFirstVisitExperience && pendingInitializedRef.current && pendingSections.length === 0) {
      pendingInitializedRef.current = false
      setShowFirstVisitExperience(false)
      setHasInitialGlowDelayElapsed(false)

      if (glowReactivateTimeoutRef.current) {
        clearTimeout(glowReactivateTimeoutRef.current)
        glowReactivateTimeoutRef.current = null
      }

      glowReactivateTimeoutRef.current = setTimeout(() => {
        glowReactivateTimeoutRef.current = null

        if (!feedbackDataRef.current) return

        setShowFirstVisitExperience(true)
      }, 90_000)
    }
  }, [showFirstVisitExperience, pendingSections.length, feedbackData])

  useEffect(() => {
    if (!showFirstVisitExperience) {
      setHasInitialGlowDelayElapsed(false)
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      return
    }

    if (isLoading || hasFetchError || !feedbackData || pendingSections.length === 0) {
      setHasInitialGlowDelayElapsed(false)
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      return
    }

    if (hasInitialGlowDelayElapsed || glowDelayTimeoutRef.current) return

    glowDelayTimeoutRef.current = setTimeout(() => {
      setHasInitialGlowDelayElapsed(true)
      glowDelayTimeoutRef.current = null
    }, 300)

    return () => {
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
    }
  }, [
    showFirstVisitExperience,
    isLoading,
    hasFetchError,
    feedbackData,
    pendingSections.length,
    hasInitialGlowDelayElapsed
  ])

  useEffect(
    () => () => {
      if (glowDelayTimeoutRef.current) {
        clearTimeout(glowDelayTimeoutRef.current)
        glowDelayTimeoutRef.current = null
      }
      if (glowReactivateTimeoutRef.current) {
        clearTimeout(glowReactivateTimeoutRef.current)
        glowReactivateTimeoutRef.current = null
      }
    },
    []
  )

  const isGlowActive =
    showFirstVisitExperience &&
    pendingSections.length > 0 &&
    !isLoading &&
    !hasFetchError &&
    Boolean(feedbackData) &&
    hasInitialGlowDelayElapsed

  const isGlintActive = useMemo(() => Object.values(glintActivity).some(Boolean), [glintActivity])

  const handleSectionGlintStateChange = useCallback((sectionId: string, state: 'idle' | 'running') => {
    setGlintActivity((prev) => {
      const nextState = state === 'running'
      const currentState = prev[sectionId] ?? false
      if (currentState === nextState) {
        return prev
      }
      if (!nextState) {
        const { [sectionId]: _, ...rest } = prev
        return rest
      }
      return {
        ...prev,
        [sectionId]: true
      }
    })
  }, [])

  const handleSectionReveal = useCallback((sectionId: string) => {
    setPendingSections((prev) => {
      if (!prev.includes(sectionId)) return prev
      return prev.filter((id) => id !== sectionId)
    })
  }, [])

  return {
    sectionIds: LATEST_SESSION_FEEDBACK_SECTION_IDS,
    pendingSections,
    isGlowActive,
    isGlintActive,
    cursorStyles: getCustomCursorStyles(isGlowActive),
    inactiveCursorStyles: getCustomCursorStyles(false),
    handleSectionGlintStateChange,
    handleSectionReveal
  }
}
